import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { logDebug } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// axios needs withCredentials for the session cookie to be sent/received.
const ax = axios.create({ baseURL: API, withCredentials: true });

const AuthContext = createContext({
  user: null,
  loading: true,
  refresh: async () => {},
  loginEmail: async () => {},
  registerEmail: async () => {},
  exchangeGoogleSession: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await ax.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    // If we're returning from Google OAuth, the URL fragment carries the
    // session_id. The dedicated AuthCallback handles that — don't hit /me
    // first or we'd 401-cache.
    if (
      typeof window !== "undefined" &&
      window.location.hash &&
      window.location.hash.includes("session_id=")
    ) {
      setLoading(false);
      return;
    }
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const loginEmail = useCallback(async (email, password) => {
    const { data } = await ax.post("/auth/login", { email, password });
    setUser(data);
    return data;
  }, []);

  const registerEmail = useCallback(async (email, password, name) => {
    const { data } = await ax.post("/auth/register", { email, password, name });
    setUser(data);
    return data;
  }, []);

  const exchangeGoogleSession = useCallback(async (sessionId) => {
    const { data } = await ax.post("/auth/google/session", { session_id: sessionId });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await ax.post("/auth/logout");
    } catch (err) {
      // server may already be down or session expired — clear UI state anyway
      logDebug("logout request failed", err);
    }
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, refresh, loginEmail, registerEmail, exchangeGoogleSession, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// Translate FastAPI error bodies into a string for safe rendering.
export function formatApiErrorDetail(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
